import { Col, Flex, Row, Spin, TableProps } from 'antd';
import Button from 'components/Button';
import AlertBar from 'components/AlertBar';
import HeaderCard from 'components/HeaderCard';
import MultiSelect from 'components/MultiSelect';
import SearchBox from 'components/SearchBox';
import RBCountryFilter, {
  useRoleBasedCountryFilter,
} from 'components/UniversalFilters/RBCountryFilter';
import useAgronomistList from 'hooks/useAgronomistList';
import useSubDistrictList from 'hooks/useSubDistrictList';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from 'routes/AllRoutes';

import FarmerGroupDetailDrawer from './components/drawer/FarmerGroupDetailDrawer';
import FarmerGroupTable from './components/FarmerGroupTable';

import useDebounceValue from 'hooks/useDebounceValue';
import usePaddyBuyerList from 'hooks/usePaddyBuyerList';
import useFarmerGroupList from './hooks/useFarmerGroupListQuery';

import { SorterResult } from 'antd/es/table/interface';
import type { FarmerGroupV2 } from 'types/response';

import { useTranslation } from 'react-i18next';
import { getStakeholderTypeForCountry } from 'utils/helpers';
import './style.scss';
import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';
import AssignAWDAg from './components/drawer/AssignAWDAg';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from 'utils/constants';

const FarmerGroup = () => {
  const [search, setSearch] = useState<string>();
  const [paddyBuyerSearch, setPaddyBuyerSearch] = useState<string>();
  const [agronomist, setAgronomist] = useState<string[]>([]);
  const [subDistrict, setSubDistrict] = useState<string[]>([]);
  const [paddyBuyer, setPaddyBuyer] = useState<string[]>([]);
  const [country, setCountry] = useRoleBasedCountryFilter();

  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<FarmerGroupV2>>({});
  const [sort, setSort] = useState('');

  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [isOpenAssign, setIsOpenAssign] = useState(false);
  const [selectedRow, setSelectedRow] = useState<FarmerGroupV2>();
  const [error] = useState();
  const [params, setParams] = useState({
    size: pageSize,
    page: currentPage - 1,
    sort: sort,
    search,
    agronomist: agronomist.join(','),
    subDistrict: subDistrict.join(','),
    paddyBuyer: paddyBuyer.join(','),
    country: country,
  });

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { subDistrictOptions, isLoading: isSubDistrictLoading } =
    useSubDistrictList(country);

  const debouncedPaddyBuyerSearch = useDebounceValue(paddyBuyerSearch!, 500);
  const { paddyBuyerOptions, isLoading: isPaddyBuyerLoading } =
    usePaddyBuyerList({ country, search: debouncedPaddyBuyerSearch });

  useEffect(() => {
    // this update the state for ANTD Table, might nothing changes here
    handlePageChange(DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE);
    // search API with parameters
    setParams({
      ...params,
      search,
      agronomist: agronomist.join(','),
      subDistrict: subDistrict.join(','),
      paddyBuyer: paddyBuyer.join(','),
      country: country,
      size: DEFAULT_PAGE_SIZE,
      page: DEFAULT_PAGE_NUMBER - 1,
    });
  }, [search, agronomist, subDistrict, paddyBuyer, country]);

  useEffect(() => {
    setParams({
      ...params,
      size: pageSize,
      page: currentPage - 1,
      sort,
    });
  }, [pageSize, currentPage, sort]);

  const farmerGroupData = useFarmerGroupList(params);
  const { agronomistFilterOptions, isLoading: isAgronomistLoading } =
    useAgronomistList(country);

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleChange: TableProps<FarmerGroupV2>['onChange'] = (
    _,
    __,
    tableSort,
  ) => {
    const sort = tableSort as SorterResult<FarmerGroupV2>;
    if (sort.order) {
      setSort(`${sort.columnKey},${sort.order === 'ascend' ? 'asc' : 'desc'}`);
    } else {
      setSort('');
    }
    setSortedInfo(sort);
  };

  const handleAction = (data: FarmerGroupV2, drawer = 'detail') => {
    setSelectedRow(data);
    if (drawer === 'assign') {
      setIsOpenAssign(true);
    } else {
      setIsOpenDetail(true);
    }
  };

  const onCloseDrawer = () => {
    setIsOpenDetail(false);
    setIsOpenAssign(false);
    setSelectedRow(undefined);
  };

  return (
    <div className="dashboard-container">
      {error && <AlertBar type="error" message={error} />}
      <HeaderCard
        headerLabel={t('farmerGroups.header')}
        headerInfo={t('farmerGroups.subHeader')}
        filters={
          <Flex gap={20} align="center" wrap="wrap">
            <RBCountryFilter
              className="select-box"
              selectedCountry={country}
              onChange={setCountry}
            />
          </Flex>
        }
      />

      <Row>
        <Col span={24} className="table-container">
          <Row className="filters">
            <SearchBox
              placeholder="Search farmer group leader name"
              onSearch={setSearch}
            />
            <MultiSelect
              label={t('common.filters.agronomist')}
              placeholder={t('common.filters.agronomistPlaceholder')}
              options={[
                { value: '-', label: 'Not Assigned Yet' },
                ...(agronomistFilterOptions?.length
                  ? agronomistFilterOptions
                  : []),
              ]}
              value={agronomist}
              onChange={setAgronomist}
              disabled={isAgronomistLoading}
              allowClear
            />
            <MultiSelect
              label={t('common.filters.subDistrict')}
              placeholder={t('common.filters.selectSubDistrict')}
              options={subDistrictOptions}
              value={subDistrict}
              onChange={setSubDistrict}
              disabled={isSubDistrictLoading}
              allowClear
            />
            <MultiSelect
              label={getStakeholderTypeForCountry(country, 'PB')}
              placeholder={t('common.select', {
                value: getStakeholderTypeForCountry(country, 'PB'),
              })}
              options={paddyBuyerOptions}
              value={paddyBuyer}
              onChange={setPaddyBuyer}
              onSearch={setPaddyBuyerSearch}
              loading={isPaddyBuyerLoading}
              showSearch
              allowClear
              notFoundContent={
                isPaddyBuyerLoading ? (
                  <Flex justify="center" align="center">
                    <Spin size="small" />
                  </Flex>
                ) : undefined
              }
            />

            <ProtectedResource permission="farmer_group:add">
              <Button
                className="create-button"
                type="primary"
                onClick={() => navigate(routes.farmerGroupCreate)}
              >
                {t('farmerGroups.addFarmerGroup')}
              </Button>
            </ProtectedResource>
          </Row>
          <FarmerGroupTable
            country={country}
            data={farmerGroupData.data?.data?.content}
            total={farmerGroupData.data?.data?.totalElements || 0}
            loading={farmerGroupData.isLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            sortedInfo={sortedInfo}
            selectedRow={selectedRow}
            handlePageChange={handlePageChange}
            handleAction={handleAction}
            handleChange={handleChange}
          />
        </Col>
      </Row>
      <FarmerGroupDetailDrawer
        country={country}
        data={selectedRow}
        open={isOpenDetail}
        onClose={onCloseDrawer}
      />
      <AssignAWDAg
        open={isOpenAssign}
        onClose={onCloseDrawer}
        data={selectedRow}
        onSuccess={() => {}}
      />
    </div>
  );
};

export default FarmerGroup;
